import {
   ErrorMessageData,
   ErrorResponse,
   ErrorUnion,
   GraphError,
   isBatchGraphError,
   isErrorMessage,
   isErrorResponse,
   isGraphError,
   isRestApiError,
   SingleGraphError,
} from './models/error-message';

function processGraphQlErrorMessage(errors: GraphError | undefined): string[] {
   const userMessages: string[] = [];
   if (errors && errors.errors) {
      let foundTransactionAbortedException = false;
      for (const error of errors.errors) {
         if (error.extensions.code === 'TRANSACTION_ABORTED') {
            continue;
         }
         if (error.extensions.data.Exception === 'TransactionException') {
            continue;
         }
         if (error.extensions.data.Exception === 'TransactionAbortedException') {
            if (foundTransactionAbortedException) {
               continue;
            } else {
               foundTransactionAbortedException = true;
            }
         }
         userMessages.push(error.extensions.data.UserMessage);
      }
   }

   return userMessages;
}

function processDuplicateEntryError(errorMessage: ErrorResponse) {
   const graphError = errorMessage?.error?.errors?.[0];

   let msg = 'The entity already exists, please try using a unique identifier.';

   if (!graphError?.message) return msg;

   const formatLabel = (input: string) =>
      input
         .replace(/([a-z])([A-Z])/g, '$1 $2') // insert space between camelCase
         .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // handle PascalCase transitions like "JSONData"
         .replace(/^./, (s) => s.toUpperCase()); // capitalize first letter

   const mysqlRegex = /Duplicate entry '([^']+)' for key '([^']+)'/;
   const mysqlMatch = graphError.message.match(mysqlRegex);

   if (mysqlMatch) {
      const value = formatLabel(mysqlMatch[1]);
      const column = mysqlMatch[2];
      const keyParts = column.split('_');
      const columnName = formatLabel(keyParts[keyParts.length - 1]);
      msg = `'${value}' already exists for '${columnName}'.`;
      return msg;
   }

   const gqlRegex = /The entity '([^']+)' already exists for ([^,.\n]+)/;
   const gqlMatch = graphError.message.match(gqlRegex);

   if (gqlMatch) {
      const value = formatLabel(gqlMatch[1]);
      const entity = formatLabel(gqlMatch[2]);
      msg = `'${value}' already exists for '${entity}'.`;
      return msg;
   }

   const userMsg = graphError?.extensions?.data?.UserMessage;
   if (userMsg) {
      return userMsg;
   }

   return msg;
}

export function processErrorMessage(errorMessage: ErrorUnion): ErrorMessageData {
   console.log(errorMessage);
   let details: string | null = null;
   const userMessages: string[] = [];

   try {
      if (isErrorMessage(errorMessage)) {
         if (errorMessage.UserMessage) {
            userMessages.push(errorMessage.UserMessage);
         }
      } else if (isRestApiError(errorMessage)) {
         if (errorMessage.UserMessage) {
            userMessages.push(errorMessage.UserMessage);
         }
         if (errorMessage.Details) {
            details = errorMessage.Details;
         }
         if (errorMessage.hasOwnProperty('userMessage')) {
            userMessages.push(errorMessage.userMessage);
         }
      } else if (isErrorResponse(errorMessage)) {
         userMessages.push(processDuplicateEntryError(errorMessage));
      } else if (isGraphError(errorMessage)) {
         userMessages.push(...processGraphQlErrorMessage((errorMessage as SingleGraphError).error));
      } else {
         if (isBatchGraphError(errorMessage)) {
            for (const error of errorMessage.error) {
               userMessages.push(...processGraphQlErrorMessage(error));
            }
         } else {
            userMessages.push(...processGraphQlErrorMessage(errorMessage));
         }
      }
   } catch {
      userMessages.push('An unknown error occurred');
      try {
         const json = JSON.stringify(errorMessage);
         userMessages.push(json);
      } catch {
         userMessages.push(String(errorMessage));
      }
   }

   if (userMessages.length === 0) {
      userMessages.push('An unknown error occurred.');
   }

   return {
      details,
      userMessages,
   };
}
